import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { D3DragEvent, Simulation } from 'd3';
import Box from '@mui/material/Box';
import { categoryColor } from '../utils/ontologyConstants';
import { GraphLink, GraphNode } from '../utils/graphHopUtils';

interface SimNode extends GraphNode, d3.SimulationNodeDatum {
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface SimLink extends d3.SimulationLinkDatum<SimNode> {
  id: string;
  predicate: string;
}

export interface KnowledgeGraphD3Props {
  nodes: GraphNode[];
  links: GraphLink[];
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  dimmedNodeIds?: Set<string>;
  searchMatchIds?: Set<string>;
}

type PositionCache = Map<string, { x: number; y: number; fx?: number | null; fy?: number | null }>;

function graphStructureKey(nodes: GraphNode[], links: GraphLink[]): string {
  const nodeIds = nodes.map((n) => n.id).sort().join('|');
  const linkIds = links
    .map((l) => `${l.source}-${l.predicate}-${l.target}`)
    .sort()
    .join('|');
  return `${nodeIds}::${linkIds}`;
}

function linkKey(link: SimLink): string {
  const src = typeof link.source === 'object' ? link.source.id : link.source;
  const tgt = typeof link.target === 'object' ? link.target.id : link.target;
  return `${src}-${link.predicate}-${tgt}`;
}

function seedNodePosition(
  node: SimNode,
  plainLinks: GraphLink[],
  cache: PositionCache,
  width: number,
  height: number
): void {
  const cached = cache.get(node.id);
  if (cached) {
    node.x = cached.x;
    node.y = cached.y;
    if (cached.fx != null) node.fx = cached.fx;
    if (cached.fy != null) node.fy = cached.fy;
    return;
  }

  for (const link of plainLinks) {
    let neighborId: string | null = null;
    if (link.source === node.id) neighborId = link.target;
    else if (link.target === node.id) neighborId = link.source;
    if (!neighborId) continue;

    const neighborPos = cache.get(neighborId);
    if (neighborPos) {
      const angle = Math.random() * Math.PI * 2;
      node.x = neighborPos.x + Math.cos(angle) * 72;
      node.y = neighborPos.y + Math.sin(angle) * 72;
      return;
    }
  }

  node.x = width / 2 + (Math.random() - 0.5) * 100;
  node.y = height / 2 + (Math.random() - 0.5) * 100;
}

function applyNodeStyles(
  nodeSelection: d3.Selection<SVGGElement, SimNode, SVGGElement, unknown>,
  selectedNodeId: string | null,
  dimmedNodeIds: Set<string> | undefined,
  searchMatchIds: Set<string> | undefined
): void {
  nodeSelection
    .select('circle')
    .attr('fill', (d) => categoryColor(d.category))
    .attr('stroke', (d) => (selectedNodeId === d.id ? '#000' : '#fff'))
    .attr('stroke-width', (d) => (selectedNodeId === d.id ? 3 : 1.5))
    .attr('opacity', (d) => {
      if (dimmedNodeIds && !dimmedNodeIds.has(d.id)) return 0.15;
      if (searchMatchIds && searchMatchIds.size > 0 && !searchMatchIds.has(d.id)) return 0.25;
      return 1;
    });

  nodeSelection
    .select('text')
    .text((d) => (d.name.length > 18 ? `${d.name.slice(0, 16)}…` : d.name))
    .attr('opacity', (d) => {
      if (dimmedNodeIds && !dimmedNodeIds.has(d.id)) return 0.2;
      return 1;
    });
}

function applyLinkStyles(
  linkSelection: d3.Selection<SVGLineElement, SimLink, SVGGElement, unknown>,
  dimmedNodeIds: Set<string> | undefined
): void {
  linkSelection.attr('opacity', (d) => {
    const src = typeof d.source === 'object' ? d.source.id : d.source;
    const tgt = typeof d.target === 'object' ? d.target.id : d.target;
    if (dimmedNodeIds && (!dimmedNodeIds.has(String(src)) || !dimmedNodeIds.has(String(tgt)))) {
      return 0.1;
    }
    return 0.5;
  });
}

export function KnowledgeGraphD3({
  nodes,
  links,
  selectedNodeId,
  onSelectNode,
  dimmedNodeIds,
  searchMatchIds,
}: KnowledgeGraphD3Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const simRef = useRef<Simulation<SimNode, SimLink> | null>(null);
  const positionCacheRef = useRef<PositionCache>(new Map());
  const structureKeyRef = useRef<string>('');
  const gRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const onSelectNodeRef = useRef(onSelectNode);
  const selectedNodeIdRef = useRef(selectedNodeId);
  const dimmedNodeIdsRef = useRef(dimmedNodeIds);
  const searchMatchIdsRef = useRef(searchMatchIds);

  onSelectNodeRef.current = onSelectNode;
  selectedNodeIdRef.current = selectedNodeId;
  dimmedNodeIdsRef.current = dimmedNodeIds;
  searchMatchIdsRef.current = searchMatchIds;

  // One-time SVG + zoom setup.
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;

    const svg = d3.select(svgEl);
    let g = svg.select<SVGGElement>('g.graph-root');
    if (g.empty()) {
      g = svg.append('g').attr('class', 'graph-root');
      const zoom = d3
        .zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.2, 4])
        .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
          g.attr('transform', event.transform.toString());
        });
      svg.call(zoom);
      svg.on('click', () => onSelectNodeRef.current(null));
    }
    gRef.current = g;
  }, []);

  // Graph structure updates — preserve positions, gently reheat simulation.
  useEffect(() => {
    const container = containerRef.current;
    const g = gRef.current;
    if (!container || !g) return;

    const width = container.clientWidth || 800;
    const height = container.clientHeight || 600;
    const cache = positionCacheRef.current;
    const structureKey = graphStructureKey(nodes, links);
    const structureChanged = structureKey !== structureKeyRef.current;
    structureKeyRef.current = structureKey;

    const simNodes: SimNode[] = nodes.map((n) => {
      const node: SimNode = { ...n };
      seedNodePosition(node, links, cache, width, height);
      return node;
    });
    const nodeById = new Map(simNodes.map((n) => [n.id, n]));
    const simLinks: SimLink[] = links
      .filter((l) => nodeById.has(l.source) && nodeById.has(l.target))
      .map((l) => ({
        ...l,
        id: l.id,
        source: nodeById.get(l.source)!,
        target: nodeById.get(l.target)!,
      }));

    const link = g
      .selectAll<SVGLineElement, SimLink>('line.link')
      .data(simLinks, (d) => linkKey(d))
      .join(
        (enter) =>
          enter
            .append('line')
            .attr('class', 'link')
            .attr('stroke', '#999')
            .attr('stroke-width', 1.5),
        (update) => update,
        (exit) => exit.remove()
      );

    const linkLabel = g
      .selectAll<SVGTextElement, SimLink>('text.link-label')
      .data(simLinks, (d) => `lbl-${linkKey(d)}`)
      .join(
        (enter) =>
          enter
            .append('text')
            .attr('class', 'link-label')
            .attr('font-size', 9)
            .attr('fill', '#666')
            .attr('text-anchor', 'middle')
            .text((d) => d.predicate),
        (update) => update.text((d) => d.predicate),
        (exit) => exit.remove()
      );

    const node = g
      .selectAll<SVGGElement, SimNode>('g.node')
      .data(simNodes, (d) => d.id)
      .join(
        (enter) => {
          const ng = enter
            .append('g')
            .attr('class', 'node')
            .style('cursor', 'pointer')
            .attr('transform', (d) => `translate(${d.x ?? width / 2},${d.y ?? height / 2})`);
          ng.append('circle').attr('r', 14);
          ng.append('text')
            .attr('dy', 28)
            .attr('text-anchor', 'middle')
            .attr('font-size', 11)
            .attr('fill', '#333');
          return ng;
        },
        (update) => update,
        (exit) => exit.remove()
      );

    applyNodeStyles(node, selectedNodeIdRef.current, dimmedNodeIdsRef.current, searchMatchIdsRef.current);
    applyLinkStyles(link, dimmedNodeIdsRef.current);

    const applyPositionsFromNodes = () => {
      link
        .attr('x1', (d) => (d.source as SimNode).x ?? 0)
        .attr('y1', (d) => (d.source as SimNode).y ?? 0)
        .attr('x2', (d) => (d.target as SimNode).x ?? 0)
        .attr('y2', (d) => (d.target as SimNode).y ?? 0);
      applyLinkStyles(link, dimmedNodeIdsRef.current);

      linkLabel
        .attr('x', (d) => (((d.source as SimNode).x ?? 0) + ((d.target as SimNode).x ?? 0)) / 2)
        .attr('y', (d) => (((d.source as SimNode).y ?? 0) + ((d.target as SimNode).y ?? 0)) / 2);

      node.attr('transform', (d) => `translate(${d.x ?? 0},${d.y ?? 0})`);
    };

    const tick = () => {
      simNodes.forEach((n) => {
        if (n.x != null && n.y != null) {
          cache.set(n.id, { x: n.x, y: n.y, fx: n.fx, fy: n.fy });
        }
      });
      applyPositionsFromNodes();
    };

    // Memory polls refresh labels/counts but keep the same node/link ids — do not
    // reheat the force simulation or nodes drift on every poll.
    if (!structureChanged && simRef.current) {
      applyPositionsFromNodes();
      return;
    }

    node.call(
      d3
        .drag<SVGGElement, SimNode>()
        .on('start', (event: D3DragEvent<SVGGElement, SimNode, SimNode>, d) => {
          if (!event.active && simRef.current) simRef.current.alphaTarget(0.2).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event: D3DragEvent<SVGGElement, SimNode, SimNode>, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event: D3DragEvent<SVGGElement, SimNode, SimNode>, d) => {
          if (!event.active && simRef.current) simRef.current.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        })
    );

    node.on('click', (event: MouseEvent, d) => {
      event.stopPropagation();
      const current = selectedNodeIdRef.current;
      onSelectNodeRef.current(d.id === current ? null : d.id);
    });

    const prevIds = new Set(Array.from(cache.keys()));
    const newNodeCount = simNodes.filter((n) => !prevIds.has(n.id)).length;

    if (simRef.current) {
      simRef.current.nodes(simNodes);
      const linkForce = simRef.current.force('link') as d3.ForceLink<SimNode, SimLink>;
      linkForce.links(simLinks);
      simRef.current.on('tick', tick);
      const alpha = newNodeCount > 0 ? 0.35 : 0.6;
      simRef.current.alpha(alpha).restart();
    } else {
      const simulation = d3
        .forceSimulation(simNodes)
        .force(
          'link',
          d3
            .forceLink<SimNode, SimLink>(simLinks)
            .id((d) => d.id)
            .distance(90)
            .strength(0.6)
        )
        .force('charge', d3.forceManyBody().strength(-280))
        .force('center', d3.forceCenter(width / 2, height / 2))
        .force('collide', d3.forceCollide(28))
        .alpha(0.6);

      simulation.on('tick', tick);
      simulation.on('end', () => {
        tick();
      });
      simRef.current = simulation;
    }

    tick();
  }, [nodes, links]);

  // Visual-only updates — no simulation restart.
  useEffect(() => {
    const g = gRef.current;
    if (!g) return;

    const node = g.selectAll<SVGGElement, SimNode>('g.node');
    applyNodeStyles(node, selectedNodeId, dimmedNodeIds, searchMatchIds);

    const link = g.selectAll<SVGLineElement, SimLink>('line.link');
    applyLinkStyles(link, dimmedNodeIds);
  }, [selectedNodeId, dimmedNodeIds, searchMatchIds]);

  useEffect(() => {
    return () => {
      simRef.current?.stop();
      simRef.current = null;
    };
  }, []);

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', minHeight: 0, bgcolor: '#fafafa' }}>
      <svg ref={svgRef} style={{ display: 'block', width: '100%', height: '100%' }} />
    </Box>
  );
}
